/**
 * The Arche Store (`/a/store`): every app on Fias in the App Store's layout —
 * Today, Apps, Games (the Games page), Top Charts, Categories, Search, and a
 * page for every app — readable with no account.
 *
 * The pages are `@fias/public-site-ui`: presentational components that also
 * render the static snapshots crawlers read. This file supplies what the app
 * knows: the router's links, the visitor, the store's tabs, and the frame —
 * the app's own header for a signed-in user, the public site's for everyone
 * else.
 *
 * While the store is switched off here (Dev Console → Store), the Arche Store
 * is the old one: this page sends people to it (`/marketplace`), so a Worktop
 * tile or a link to the Arche Store never lands on a dark page.
 */
import { useMemo, type ReactElement } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  GamesSiteProvider,
  PublicSiteFrame,
  StoreLayout,
  StoreSurface,
  type SiteLinkProps,
  type StoreTab,
} from '@fias/public-site-ui';
import { useAuth } from '@/auth/hooks/useAuth';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useGamesPageAvailable } from '@/lib/games-availability';
import { useStorePageAvailable } from '@/lib/store-availability';
import { ArcheLayout } from '../../components/ArcheLayout';
import { STORE_CATEGORY_LABELS, STORE_GENRE_LABELS } from './store-labels';
import {
  StoreAppPage,
  StoreAppsPage,
  StoreCategoriesPage,
  StoreCategoryPage,
  StoreLoading,
  StoreNotFoundPage,
  StoreSearchPage,
  StoreStoryPage,
  StoreTodayPage,
  StoreTopPage,
} from './pages/StorePages';

/** The router's link, drawn with the package's link props (same markup as `<a>`). */
function RouterLink({ href, children, ...rest }: SiteLinkProps): ReactElement {
  return (
    <Link to={href} {...rest}>
      {children}
    </Link>
  );
}

/** Which tab a store address belongs to. */
function tabOf(pathname: string): StoreTab {
  const rest = pathname.replace(/^\/a\/(store|arche_marketplace)\/?/, '');
  const first = rest.split('/')[0] ?? '';
  if (first === 'apps' || first === 'app') return 'apps';
  if (first === 'top') return 'top';
  if (first === 'categories' || first === 'category') return 'categories';
  if (first === 'search') return 'search';
  return 'today';
}

/** The old store's page for an arche: the Advanced search's detail page. */
function oldDetailPath(archeId: string): string {
  return `/marketplace/arches/${encodeURIComponent(archeId)}`;
}

/**
 * `info/<archeId>` — where an in-app "Info" link lands: the arche's store page
 * (or Games page) when it is on the store, otherwise the old detail page.
 */
function StoreInfoRedirect(): ReactElement {
  const { archeId = '' } = useParams();
  const address = useQuery({
    queryKey: queryKeys.store.address(archeId, null),
    queryFn: () => api.getStoreAddress(archeId),
    retry: false,
    staleTime: 60_000,
  });
  if (address.isPending) return <StoreLoading />;
  return <Navigate to={address.data?.pagePath ?? oldDetailPath(archeId)} replace />;
}

function StoreRoutes(): ReactElement {
  return (
    <Routes>
      <Route path="info/:archeId" element={<StoreInfoRedirect />} />
      <Route index element={<StoreTodayPage />} />
      <Route path="apps" element={<StoreAppsPage />} />
      <Route path="top" element={<StoreTopPage />} />
      <Route path="categories" element={<StoreCategoriesPage />} />
      <Route path="category/:categoryId" element={<StoreCategoryPage />} />
      <Route path="story/:slug" element={<StoreStoryPage />} />
      <Route path="app/:slug" element={<StoreAppPage />} />
      <Route path="search" element={<StoreSearchPage />} />
      <Route path="*" element={<StoreNotFoundPage />} />
    </Routes>
  );
}

export function StoreArchePage(): ReactElement {
  const { isAuthenticated } = useAuth();
  const available = useStorePageAvailable();
  const gamesShown = useGamesPageAvailable() === true;
  const { pathname } = useLocation();
  // "Now" for the page's lifetime: what "new" and Today's date are measured from.
  const now = useMemo(() => Date.now(), []);

  if (available === false) {
    // An "Info" link still reaches the arche's old detail page.
    const info = /\/info\/([^/]+)$/.exec(pathname);
    return (
      <Navigate to={info ? oldDetailPath(decodeURIComponent(info[1])) : '/marketplace'} replace />
    );
  }

  const body =
    available === null ? (
      <StoreLoading />
    ) : (
      <StoreLayout current={tabOf(pathname)} gamesShown={gamesShown}>
        <StoreRoutes />
      </StoreLayout>
    );
  const site = (
    <GamesSiteProvider
      Link={RouterLink}
      signedIn={isAuthenticated}
      now={now}
      genreLabels={STORE_GENRE_LABELS}
      categoryLabels={STORE_CATEGORY_LABELS}
      storeShown={available === true}
      gamesShown={gamesShown}
    >
      <StoreSurface>
        {isAuthenticated ? body : <PublicSiteFrame current="store">{body}</PublicSiteFrame>}
      </StoreSurface>
    </GamesSiteProvider>
  );

  if (isAuthenticated) {
    return (
      <ArcheLayout displayMode="normal" showSidebar={false}>
        {site}
      </ArcheLayout>
    );
  }
  return site;
}

export default StoreArchePage;
